import {action, observable} from "mobx";
import {NoticeTypeEnum} from "../models/notice";
import {IShippingInfoDto} from "../models/shipping/shipping-info";
import {IShippingMethodDto} from "../models/shipping/shipping-method";
import BaseStore from "./base/base-store";
import RootStore from "./root-store";

export default class ShippingStore extends BaseStore {

    @observable
    public isFetchingMethods: boolean = false;

    @observable
    public methods: IShippingMethodDto[];

    @observable
    public isFetchingDestinations: boolean = false;

    @observable
    public destinations: string[];

    @observable
    public selectedMethod: IShippingMethodDto;

    @observable
    public selectedDestination: string;

    @observable
    public isFetchingInfo: boolean = false;

    @observable
    public shippingInfo: IShippingInfoDto;

    constructor(rootStore: RootStore) {
        super(rootStore);
    }

    public async getDestinationsByMethod() {
        const rootStore = this.rootStore;
        const countryId = rootStore.countryStore.selectedCountry.id;
        const selectedMethod = this.selectedMethod;
        const methodId = selectedMethod && selectedMethod.id;

        if (countryId && methodId) {
            try {
                this.startFetchingDestinations();
                const result = await rootStore.shippingApi.getDestinationsByMethod(countryId, methodId);

                if (result.data.valid) {
                    this.setDestinations(result.data.result);
                } else {
                    Object.values(result.data.errors).map((error) => {
                        this.notify(NoticeTypeEnum.Failure, error);
                    })
                }
            } catch (e) {
                this.notify(NoticeTypeEnum.Failure, e);
            } finally {
                this.finishFetchingDestinations();
            }
        }
    }

    public async getShippingInfo() {
        const rootStore = this.rootStore;
        const countryId = rootStore.countryStore.selectedCountry.id;
        const selectedMethod = this.selectedMethod;
        const methodId = selectedMethod && selectedMethod.id;

        if (countryId && methodId) {
            const request = {
                countryId: countryId,
                methodId: methodId,
                categoryIds: rootStore.cartStore.cart.rows.map((row) => row.item.product.category.id)
            };

            try {
                this.startFetchingInfo();
                const result = await rootStore.shippingApi.getShippingInfo(request);
                this.setShippingInfo(result.data);
            } catch (e) {
                this.notify(NoticeTypeEnum.Failure, e);
            } finally {
                this.finishFetchingInfo();
            }
        } else {
            this.resetShippingInfo();
        }
    }

    @action
    public resetSelectedMethod() {
        this.resetShippingInfo();
        this.selectedMethod = null;
        this.resetDestinationsList();
    }

    @action
    public resetMethodsList() {
        this.methods = null;
        this.resetSelectedMethod();
    }

    public async selectMethod(methodId: number) {
        this.setSelectedMethod(methodId);

        if (!this.selectedMethod) {
            this.resetSelectedMethod();
        }

        await this.rootStore.shippingStore.getShippingInfo();
    }

    public async searchMethods() {
        const rootStore = this.rootStore;
        const selectedCountry = rootStore.countryStore.selectedCountry;
        const countryId = selectedCountry && selectedCountry.id;

        if (countryId) {
            const categoryIds = rootStore.cartStore.cart.rows.map((row) => row.item.product.category.id);

            const request = {
                countryId: countryId,
                categoryIds: categoryIds
            };

            try {
                this.startFetchingMethods();
                const result = await rootStore.shippingApi.searchMethods(request);
                this.setMethods(result.data);
            } catch (e) {
                this.notify(NoticeTypeEnum.Failure, e);
            } finally {
                this.finishFetchingMethods();
            }
        }
    }

    public initMethodsList(): any {
        const allMethods = [];
        allMethods.push({
            id: "",
            name: `-- ${this.rootStore.translationStore.translate("checkout.choose-shipping-method", "Choose a method")} --`
        });

        if (this.methods) {
            for (const method of this.methods) {
                allMethods.push(method);
            }
        }

        return allMethods;
    }

    public initDestinationsList(): any {
        const allDestinations = [];
        allDestinations.push({
            key: "",
            value: `-- ${this.rootStore.translationStore.translate("checkout.choose-shipping-destination", "Choose a destination")} --`
        });

        if (this.destinations) {
            for (const destination of this.destinations) {
                allDestinations.push({
                    key: destination,
                    value: destination
                });
            }
        }

        return allDestinations;
    }

    @action
    public resetDestinationsList() {
        this.selectedDestination = null;
        this.destinations = null;
        this.initDestinationsList();
    }

    @action
    private setSelectedMethod(id: number) {
        this.selectedMethod = this.methods.find(x => x.id === id);
    }

    @action
    private setMethods(methods: IShippingMethodDto[]) {
        this.selectedMethod = null;
        this.methods = methods;
    }

    @action
    private setDestinations(destinations: string[]) {
        this.selectedDestination = null;
        this.destinations = destinations;
    }

    @action
    private setShippingInfo(info: IShippingInfoDto) {
        this.shippingInfo = info;
    }

    @action
    private resetShippingInfo() {
        this.shippingInfo = null;
    }

    @action
    private startFetchingDestinations() {
        this.isFetchingDestinations = true;
    }

    @action
    private finishFetchingDestinations() {
        this.isFetchingDestinations = false;
    }

    @action
    private startFetchingInfo() {
        this.isFetchingInfo = true;
    }

    @action
    private finishFetchingInfo() {
        this.isFetchingInfo = false;
    }

    @action
    private startFetchingMethods() {
        this.isFetchingMethods = true;
    }

    @action
    private finishFetchingMethods() {
        this.isFetchingMethods = false;
    }
}



// WEBPACK FOOTER //
// ./src/stores/shipping-store.ts